import gsap, { ScrollTrigger, SplitText } from "@/lib/gsap";
import React, { useEffect, useRef } from "react";
import CoverImage from "./CoverImage";
import Illustration from "./Illustration";
import Buttons from "./Illustration/Buttons";
import { Container, CoverContainer, Excerpt, ExcerptWrapper, Heading, Text } from "./styles";
import { ButtonsProps } from "./Illustration/Buttons/Buttons";
import { debounce, uniqueId } from "lodash";
import _SplitText from "gsap/SplitText";
import SimpleRichText from "@/components/SimpleRichText";

type Props = {
  heading?: any;
  excerpt?: any;
  excerptPortable?: Record<string, any>;
  buttons?: ButtonsProps["buttons"];
  image?: {
    asset?: any;
    alt?: any;
    position?: any;
  };
  illustrations?: any[];
};

const SectionWithIllustrations: React.FC<Props> = (props) => {
  const { heading, excerpt, excerptPortable, image, illustrations, buttons } =
    props;

  const containerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const excerptRef = useRef<HTMLParagraphElement>(null);
  const excerptWrapperRef = useRef<HTMLParagraphElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const headingTextRef = useRef<_SplitText | null>(null);
  const excerptTextRef = useRef<_SplitText | null>(null);

  useEffect(() => {
    const revertText = debounce(() => {
      if (headingTextRef.current && excerptTextRef.current) {
        headingTextRef.current?.revert();
        excerptTextRef.current?.revert();
      }
    }, 30);

    // Create closure around mutable refs
    const container = containerRef.current;
    const heading = headingRef.current;
    const excerpt = excerptRef.current;
    const excerptWrapper = excerptWrapperRef.current;
    const imageWrapper = imageWrapperRef.current;
    const buttons = buttonsRef.current;

    // When called with create, ScrollTrigger will create a new instance of ScrollTrigger
    // Don't forget to kill that instance when the component unmounts.
    const containerTriggerId = uniqueId("container");

    // Enclosing the code to establish group tasks which needs to be executed in a specific order.
    const prepareAnimation = () => {
      // Prepare elements
      if (heading) {
        headingTextRef.current = new SplitText(heading, {
          type: "lines",
          linesClass: "lines",
        });
        gsap.set(heading, { opacity: 1.0 });
      }

      if (excerpt) {
        excerptTextRef.current = new SplitText(excerpt, {
          type: "lines",
          linesClass: "lines",
        });
        gsap.set(excerpt, { opacity: 1.0 });
      }

      window.addEventListener("resize", revertText);

      if (buttons) {
        gsap.set(buttons, { opacity: 0 });
      }
    };
    prepareAnimation();

    const runAnimation = () => {
      if (heading && headingTextRef.current) {
        const headingText = headingTextRef.current;
        const headingTextTweenIn = gsap.to(headingText.lines, {
          opacity: 0.16,
          duration: 0.3,
          paused: true,
        });
        const headingTextTweenOut = gsap.to(headingText.lines, {
          opacity: 0,
          y: 0,
          duration: 0.3,
          paused: true,
        });
        ScrollTrigger.create({
          id: containerTriggerId,
          trigger: container,
          start: "top center",
          end: "80% top",
          // scrub: 1,
          onEnter: () => headingTextTweenIn.play(),
          onEnterBack: () => headingTextTweenOut.reverse(),
          onLeave: () => headingTextTweenOut.play(),
          onLeaveBack: () => headingTextTweenIn.reverse(),
        });
      }

      if (excerpt && excerptTextRef.current) {
        const excerptText = excerptTextRef.current;
        gsap.to(excerptText.lines, {
          opacity: 1.0,
          y: 0,
          stagger: { amount: 0.3 },
          scrollTrigger: {
            trigger: excerpt,
            toggleActions: "play pause resume reverse",
            start: "top 75%",
          },
        });
      }

      if (excerptWrapper) {
        gsap.to(excerptWrapper, {
          opacity: 1.0,
          y: 0,
          scrollTrigger: {
            trigger: excerptWrapper,
            toggleActions: "play pause resume reverse",
            start: "top 75%",
          },
        });
      }

      if (buttons) {
        gsap.to(buttons, {
          opacity: 1,
          delay: 0.5,
        });
      }

      if (imageWrapper) {
        gsap.to(imageWrapper, {
          opacity: 1.0,
          y: 0,
          scrollTrigger: {
            trigger: imageWrapper,
            toggleActions: "play pause resume reverse",
            start: "top 75%",
          },
        });
      }
    };

    runAnimation();

    return function cleanup() {
      window.removeEventListener("resize", revertText);
      gsap.killTweensOf([container, heading, excerpt, imageWrapper, buttons]);
      ScrollTrigger.getById(containerTriggerId)?.kill();
    };
  }, []);

  return (
    <Container ref={containerRef}>
      {heading && <Heading ref={headingRef}>{heading}</Heading>}
      <CoverContainer>
        <Text>
          {excerpt && (
            <Excerpt
              ref={excerptRef}
              dangerouslySetInnerHTML={{
                __html: excerpt.replace(/\n/g, "<br />"),
              }}
            ></Excerpt>
          )}
          {excerptPortable && (
            <ExcerptWrapper ref={excerptWrapperRef}>
              <SimpleRichText body={excerptPortable} />
            </ExcerptWrapper>
          )}
          {buttons && <Buttons ref={buttonsRef} buttons={buttons} />}
        </Text>
        <CoverImage ref={imageWrapperRef} image={image} />
      </CoverContainer>
      {illustrations?.map((illustration, index) => {
        if (typeof illustration?._key !== "string") return null;
        return (
          <Illustration
            key={illustration._key}
            index={index}
            {...illustration}
          />
        );
      })}
    </Container>
  );
};

export default SectionWithIllustrations;
